<template>
  <div v-if="props.enable">
    <h4
      v-if="props.sidePanelOpen && hasPrevPricingData"
      class="pricing-data-title"
    >
      Price History
    </h4>
    <h4 v-else class="pricing-data-title">
      <span v-if="props.sidePanelOpen">No Historical Pricing Data</span>
    </h4>

    <highcharts
      v-if="props.sidePanelOpen && hasPrevPricingData"
      id="materialPriceHistory"
      ref="materialChartElement"
      class="hc"
      :constructor-type="'stockChart'"
      :options="chartOptions"
    ></highcharts>
  </div>

  <div v-else>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  toRefs,
  watch,
  onMounted,
  toRaw,
} from "vue";
import type { Ref, PropType } from "vue";
import {
  useGetQuotesWithPriceHistoryQuery,
  Order_By,
  Quote_Status_Dataset_Enum,
} from "gql/api";
import type {
  UIMaterial,
  GenericPropType,
} from "@/components/quotes/types";

const importsWithContextMenu = async () => {
  // TODO Get context menu working for fullscreen and png export
  const { default: Highcharts } = await import("highcharts/highstock");
  const { default: Exporting } = await import("highcharts/modules/exporting");
  const { default: FullScreen } = await import("highcharts/modules/full-screen");

  // Not callable apparently, where is the init function
  // Exporting(Highcharts);
  // FullScreen(Highcharts);
};

importsWithContextMenu()


// example type: Ref<Array<string>>

const props = defineProps({
  materialRef: {
    type: Object as PropType<UIMaterial | null>,
    default: null
  },
  pricingData: {
    type: Object as PropType<GenericPropType>,
    default: null
  },
  sidePanelOpen: {
    type: Boolean,
    default: false
  },
  enable: {
    type: Boolean,
    default: false
  }
});



const { materialRef, pricingData, sidePanelOpen, enable } = toRefs(props);

const emit = defineEmits({});


const targetMaterialNumber = computed(() => {
  if (materialRef && materialRef.value) {
    return materialRef.value.number;
  } else {
    return "NOMATCHZZZZZZ1234";
  }
});

// quotes this material number was on that were won
const { data: getQuotes, executeQuery: refetchQuotes } = useGetQuotesWithPriceHistoryQuery({
  pause: computed(() => !targetMaterialNumber.value || targetMaterialNumber.value === "NOMATCHZZZZZZ1234"),
  variables: computed(() => ({
    limit: 100,
    offset: 0,
    order_by: [{ updated_at: Order_By.Asc }],
    where: {
      status: { _eq: Quote_Status_Dataset_Enum.Won },
      quote_line_items: {
        erp_material_number: { _eq: targetMaterialNumber.value },
      },
    },
  }))
});

const hasPrevPricingData = computed(() => {
  return (getQuotes.value?.quotes?.length || 0) > 0
});

const quotesList = computed(() => {
  if (getQuotes.value?.quotes) {
    return [...getQuotes.value?.quotes];
  }
  return [];
});

type SeriesItem = {
  x: number,
  y: number,
  margin: number,
  customer: string | null | undefined,
};

const buildDateSeries = () => {
  const seriesData: SeriesItem[] = [];

  quotesList.value.forEach((quote) => {
    quote.quote_line_items.forEach((qli) => {
      if (qli.erp_material_number === targetMaterialNumber.value) {
        const wonDate = new Date(quote.updated_at)

        const lineObj = {
          x: wonDate.getTime(),
          y: qli.price_override,
          margin: qli.margin,
          customer: quote.erp_customer_name,
        }

        seriesData.push(lineObj);
      }
    })
  })

  // Sort data by timestamp (x value)
  seriesData.sort((a, b) => a.x - b.x)

  return seriesData
};

const chartOptions = ref<Highcharts.Options | null>(null)

export type ExtendedTooltipContext = {
  x: number;
  y: number;
  point: Highcharts.Point & SeriesItem;
  [key: string]: any;
};

const setupChartOptions = () => {
  chartOptions.value = {
    chart: {
      type: "line",
      backgroundColor: "#FFF",
    },
    title: {
      text: "Material Price History",
    },
    xAxis: {
      type: "datetime",
      title: {
        text: "Date",
      },
    },
    yAxis: {
      title: {
        text: "Price",
      },
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle'
    },
    series: [{
      type: "line",
      name: targetMaterialNumber.value || "name not found",
      data: buildDateSeries(),
    }],
    tooltip: {
      formatter: function (this: ExtendedTooltipContext) {
        const point = this.point as Highcharts.Point & SeriesItem;
        return `
          ${point.customer}<br>
          Date: ${new Date(point.x).toLocaleDateString("en-US")}<br>
          Price: ${point.y && point.y.toLocaleString()}<br>
          Margin: ${point.margin}%
        `;
      } as unknown as Highcharts.TooltipFormatterCallbackFunction,
    },
    credits: {
      enabled: false,
    },
  };
}

watch(getQuotes, (newList, oldList) => {
  setupChartOptions()
}, { immediate: true, deep: true});

const n = (num: any) => Number(num);

const createChartExampleData = () => {
  // Timestamps
  const d1 = "4/26/2024".split("/")
  const d2 = "4/27/2024".split("/")
  const d3 = "4/28/2024".split("/")
  const t1 = new Date(n(d1[2]), n(d1[0]) - 1, n(d1[1])).getTime();
  const t2 = new Date(n(d2[2]), n(d2[0]) - 1, n(d2[1])).getTime();
  const t3 = new Date(n(d3[2]), n(d3[0]) - 1, n(d3[1])).getTime();

  const chartData = [
    {
      data: [
        [t1, 1],
        [t2, 2],
        [t3, 3],
      ],
    },
  ]

  return chartData
};

const createStaticExampleData = () => {
  const chartDataStatic = [
    {
      data: [1, 2, 3]
    },
    {
      data: [8, 5, 2]
    }
  ];

  return chartDataStatic;
};
</script>

<style scoped lang="scss">
/* Price History / No Historical Pricing Data title — matches the Material
   Results heading so it stays readable in light and dark mode (was hardcoded
   near-black, invisible on the dark panel). */
h4.pricing-data-title {
  color: var(--text-color);
  position: relative;
  margin: 25px 0 0;
  margin-left: 12px;
  padding: 15px 0 8px 0;
  font-size: 20px;
  font-weight: 700;
}

.empty-icon {
  position: absolute;
}
</style>
